import axios from 'axios';
import _ from 'lodash';
import { FAVORITES_TYPE_PERSONALITY } from '@foxcorp/delta-web-analytics';
import isEmail from 'validator/lib/isEmail';
import cookie from 'react-cookie';
import { v1Override } from 'helpers/ContentHelper';
import { getCookie, removeCookie } from 'helpers/Storage';
import { format, FORMAT_ISO, stringToDate, startOfDay } from 'helpers/DateTimeHelper';
import { isBrowserIE } from 'helpers/GlobalHelper';

const COOKIE_NAME = '__Secure-FOXACCT';
const ANONYMOUS_COOKIE_NAME = 'FOXANONACCT';
const DEFAULT_SIGNUP_BIRTHDATE_PLACEHOLDER = 'Birthdate';
export const DEFAULT_SIGNUP_STATE = {
  signupFirstName: '',
  signupLastName: '',
  signupEmail: '',
  signupPassword: '',
  signupFirstNameError: null,
  signupLastNameError: null,
  signupFullNameError: null,
  signupPasswordError: null,
  signupTermsError: null,
  signupEmailError: null,
  signupEmailExistsError: null,
  signupSocialEmailExistsError: null,
  signupGenericError: null,
  facebookGenericError: null,
  signupBirthdate: null,
  signupBirthdateIsoFormat: null,
  signupGenderDropdownOpen: false,
  signupGender: null,
  signupGenderVisualName: null,
  signupGenderError: null,
  signupBirthdateError: null,
  signupBirthdatePlaceholder: DEFAULT_SIGNUP_BIRTHDATE_PLACEHOLDER,
  signupAgeError: null,
  signupBirthdateInvalidError: null,
};
export const DEFAULT_SIGNIN_STATE = {
  signinEmail: '',
  signinPassword: '',
  signinGenericError: null,
  signinEmailError: null,
  signinPasswordError: null,
  signinMigrationDropdownOpen: false,
};
const DEFAULT_LOGOUT_STATE = {
  logoutInProcess: false,
  logoutTimedOut: false,
};
const DEFAULT_EDIT_STATE = {
  editEmail: '',
  editEmailError: null,
  editFirstName: '',
  editFirstNameError: false,
  editGenericError: null,
  editLastName: '',
  editLastNameError: false,
};
export const DEFAULT_CHANGE_PASSWORD_STATE = {
  changePasswordPassword: '',
  changePasswordNewPassword: '',
  changePasswordShowPassword: false,
  changePasswordShowNewPassword: false,
  changePasswordGenericError: null,
  changePasswordPasswordError: null,
  changePasswordNewPasswordError: null,
};

export const DEFAULT_CREATE_PASSWORD_STATE = {
  createPasswordNewPassword: '',
  createPasswordShowNewPassword: false,
  createPasswordGenericError: null,
  createPasswordNewPasswordError: null,
};

export const DEFAULT_LINK_ACCOUNT_STATE = {
  createPasswordNewPassword: '',
  createPasswordShowNewPassword: false,
  createPasswordGenericError: null,
  createPasswordNewPasswordError: null,
};

const DEFAULT_MANAGE_FAVORITES_STATE = {};
const DEFAULT_FORGOT_PASSWORD_STATE = {
  forgotPasswordError: null,
  genericForgotPasswordError: null,
};
const DEFAULT_FACEBOOK_LINKING_STATE = {
  facebookLinkingGenericError: null,
  facebookLinkingPasswordError: null,
  facebookLinkingEmailError: null,
  facebookLinkingEmail: '',
  facebookLinkingPassword: '',
  facebookLinkingShowPassword: false,
};
export const DEFAULT_FACEBOOK_PASSWORD_LINKING_STATE = {
  facebookPasswordLinkShowPassword: false,
  facebookPasswordLinkPassword: '',
  facebookPasswordLinkGenericError: null,
  facebookPasswordLinkPasswordError: null,
};
export const DEFAULT_PARENTAL_CONTROLS_STATE = {
  parentalControlsPasswordVerified: false,
  parentalControlsPassword: '',
  parentalControlsShowPassword: false,
  parentalControlsPasswordError: null,
  parentalControlsNewTvRatingIndex: null,
  parentalControlsNewMovieRatingIndex: null,
};
export const DEFAULT_OVERLAY_STATE = {
  showPerks: false,
  showSignin: false,
  showSignup: false,
  showSignupSuccess: false,
  showAccountMain: false,
  showForgotPassword: false,
  showForgotPasswordSuccess: false,
  showSignOut: false,
  showDeleteAccount: false,
  showEditProfile: false,
  showEditAccount: false,
  showChangePassword: false,
  showCreatePassword: false,
  showManageFavorites: false,
  showResetPassword: false,
  showResetPasswordSuccess: false,
  showForgotPasswordLanding: false,
  showFacebookLinking: false,
  showFacebookPasswordLink: false,
  showFacebookLoginPasswordLink: false,
  showLinkAccount: false,
  showParentalControls: false,
  entryOverlay: null,
  showFXPlus: false,
};
export const DEFAULT_STATE = {
  ...DEFAULT_SIGNUP_STATE,
  ...DEFAULT_SIGNIN_STATE,
  ...DEFAULT_LOGOUT_STATE,
  ...DEFAULT_EDIT_STATE,
  ...DEFAULT_OVERLAY_STATE,
  ...DEFAULT_CHANGE_PASSWORD_STATE,
  ...DEFAULT_MANAGE_FAVORITES_STATE,
  ...DEFAULT_FORGOT_PASSWORD_STATE,
  ...DEFAULT_FACEBOOK_LINKING_STATE,
  ...DEFAULT_FACEBOOK_PASSWORD_LINKING_STATE,
  onCloseResetToBasePath: false,
};

const getGenderOptions = language => {
  const {
    profileCreate_genderOptionFemale: optionFemale,
    profileCreate_genderOptionMale: optionMale,
    profileCreate_genderOptionOther: optionOther,
  } = language;
  return [
    {
      name: optionMale,
      value: 'm',
    },
    {
      name: optionFemale,
      value: 'f',
    },
    {
      name: optionOther,
      value: 'o',
    },
  ];
};

const DEFAULT_ERROR_MESSAGE = 'An error has occurred';
export const passwordErrors = value => {
  if (!value) return 'You must provide a valid password';
  if (value.length < 6) return 'Password needs to be 6 or more characters long';
  return null;
};
export const isValidPassword = value => !passwordErrors(value);
export const isValidEmail = value => !!(value && isEmail(value));
export const onEnterKeyPress = (e, action) => {
  if (e.key === 'Enter' && action) {
    action();
  }
};

export function convertTimestamp(unixTimestamp) {
  if (!unixTimestamp) return null;
  return new Date(unixTimestamp);
}

function saveCookie(data, cookieName, secure) {
  const expires = convertTimestamp(_.get(data, 'tokenExpiration'));
  if (expires && !secure) return cookie.save(cookieName, data, { path: '/', expires });
  if (expires && secure)
    return cookie.save(cookieName, data, { path: '/', expires, secure: true, sameSite: true });
  return false;
}
export function getAccountCookie() {
  return getCookie({ name: COOKIE_NAME });
}
export function getAnonymousAccountCookie() {
  return getCookie({ name: ANONYMOUS_COOKIE_NAME });
}
export function setAnonymousAccountCookie(data) {
  return saveCookie(data, ANONYMOUS_COOKIE_NAME);
}
export function setAccountCookie(data) {
  return saveCookie(data, COOKIE_NAME, true);
}
export function removeAccountCookie() {
  return removeCookie({
    key: COOKIE_NAME,
    options: { path: '/', secure: true, sameSite: true },
  });
}

export function removeAnonymousAccountCookie() {
  return removeCookie({
    key: ANONYMOUS_COOKIE_NAME,
    options: { path: '/' },
  });
}
export function handleError(error, req, routeRequired = true) {
  const { route } = req;
  const message =
    error?.response?.data?.errorMessage ||
    _.get(error, 'response.data.message') ||
    error?.message ||
    _.get(error, 'request.data.message');
  const errorCode = _.get(error, 'response.data.errorCode', null);
  const statusCode = _.get(error, 'response.data.statusCode', null);
  const is404Error = errorCode === 404 || statusCode === 404;
  const detail = _.get(error, 'response.data.detail') || _.get(error, 'detail');
  const errorType = error?.response?.data?.errorType;
  if ((routeRequired && !route) || !message)
    return { error: { message: 'Sorry, we have encountered a problem!', errorCode } };
  console.error(`Could not get data for route ${route}`, message);
  return { error: { message, errorCode, errorType, detail, is404Error } };
}

export async function register(req, body, cb) {
  const { profile } = req;
  const {
    password,
    email,
    facebookToken,
    appleToken,
    googleToken,
    isGoogleSignIn,
    isGoogleOneTap,
    link,
  } = body;
  const googleBody = {
    googleToken,
    email,
    password,
    additionalHeaders: v1Override,
  };
  const isGoogle = isGoogleOneTap || isGoogleSignIn;

  if (!facebookToken && !appleToken && !googleToken) {
    const missingRequiredData = !password || !email;
    const invalidPassword = !isValidPassword(password);
    const invalidEmail = !isEmail(email || '');

    if (missingRequiredData) return cb({ error: { message: 'missing required data' } });
    if (invalidPassword) return cb({ error: { message: 'invalid password' } });
    if (invalidEmail) return cb({ error: { message: 'invalid email' } });
  }

  const profileBody = {
    ...body,
    additionalHeaders: v1Override,
  };

  try {
    let data;
    if (facebookToken) {
      data = await profile.facebookRegister(profileBody);
    } else if (appleToken && !link) {
      data = await profile.appleRegister(profileBody);
    } else if (appleToken && link) {
      data = await profile.appleLink(profileBody);
    } else if (isGoogle) {
      data = await profile.googleLink(googleBody);
    } else {
      data = await profile.register(profileBody);
    }
    return this.setState({ account: data, anonymousAccount: null }, () => {
      setAccountCookie(data);
      removeAnonymousAccountCookie();
      return cb({ success: true, account: data });
    });
  } catch (error) {
    const { errorCode, message } = error;
    if (errorCode !== 200) return cb({ error: { errorCode, message } });
    cb(handleError(error, req));
  }
  return null;
}

// TODO remove this! This IE is temporary til IE is sunset
export async function registerIE(req, body, cb) {
  const { route, headers } = req;
  const { password, email } = body;
  const missingRequiredData = !route || !headers || !password || !email;
  const invalidPassword = !isValidPassword(password);
  const invalidEmail = !isEmail(email);
  if (missingRequiredData) return cb({ error: { message: 'missing required data' } });
  if (invalidPassword) return cb({ error: { message: 'invalid password' } });
  if (invalidEmail) return cb({ error: { message: 'invalid email' } });
  try {
    const response = await axios.post(route, body, headers);
    const data = _.get(response, 'data');
    const status = _.get(response, 'status');
    const errorCode = _.get(data, 'errorCode', null);
    const message = _.get(data, 'message', DEFAULT_ERROR_MESSAGE);
    if (!data || status !== 200) return cb({ error: { errorCode, message } });
    return this.setState({ account: data, anonymousAccount: null }, () => {
      setAccountCookie(data);
      removeAnonymousAccountCookie();
      return cb({ success: true, account: data });
    });
  } catch (error) {
    cb(handleError(error, req));
  }
  return null;
}

export async function verify(req, body, cb) {
  const { route, headers } = req;
  const { requestId, headerAuthorization } = body;
  if (headerAuthorization) {
    headers.headers.Authorization = headerAuthorization;
  }

  const isMissingRequestId = !!requestId;
  if (!isMissingRequestId)
    return cb({
      success: false,
      error: {
        message: 'missing request id',
      },
    });
  try {
    const response = await axios.post(route, body, headers);
    return cb && cb({ success: true, data: response.data });
  } catch (error) {
    return cb && cb(handleError(error, req));
  }
}

export async function noVerify(req, body, cb) {
  const { route, headers } = req;
  try {
    const response = await axios.post(route, body, headers);
    return cb && cb({ success: true, data: response.data });
  } catch (error) {
    return cb && cb(handleError(error, req));
  }
}

export async function checkStatus(profile, body) {
  let response = null;
  if (!isBrowserIE()) {
    response = await profile.status({ email: body?.userEmail });
  }
  return response;
}

export async function login(req, profile, body, cb) {
  const { route, headers } = req;
  const { email, password, facebookToken, appleToken } = body;
  const missingAccountData = !email && !password;
  const missingRequiredData =
    !route || !headers || (missingAccountData && !facebookToken && !appleToken);
  if (missingRequiredData) return cb({ error: { message: 'missing required data' } });

  const invalidPassword = password && !isValidPassword(password);
  const invalidEmail = email && !isEmail(email);
  if (invalidPassword) return cb({ error: { message: 'invalid password' } });
  if (invalidEmail) return cb({ error: { message: 'invalid email' } });
  // TODO remove this! This IE is temporary til IE is sunset
  if (isBrowserIE() || appleToken || facebookToken) {
    try {
      const response = await axios.post(route, body, headers);
      const data = _.get(response, 'data');
      const status = _.get(response, 'status');
      const dataStatus = _.get(data, 'status', status);
      const errorCode = _.get(data, 'errorCode', dataStatus);
      const message = _.get(data, 'message', DEFAULT_ERROR_MESSAGE);
      const authorizationToken = _.get(data, 'accessToken', null);
      if (!data || dataStatus !== 200) return cb({ error: { errorCode, message } });
      return this.setState(
        {
          account: data,
          anonymousAccount: null,
          authorizationToken,
        },
        () => {
          setAccountCookie(data);
          removeAnonymousAccountCookie();
          return cb({ success: true, account: data });
        },
      );
    } catch (error) {
      return cb(handleError(error, req));
    }
  } else {
    try {
      let response = null;
      if (facebookToken) {
        response = await profile.facebookLogin({
          facebookToken,
          additionalHeaders: v1Override,
        });
      } else {
        response = await profile.login({
          email,
          password,
          additionalHeaders: v1Override,
        });
      }
      const authorizationToken = response?.accessToken || null;
      return this.setState(
        {
          account: response,
          anonymousAccount: null,
          authorizationToken,
        },
        () => {
          setAccountCookie(response);
          removeAnonymousAccountCookie();
          return cb({ success: true, account: response });
        },
      );
    } catch (error) {
      const {
        status,
        dataStatus = status,
        errorCode = dataStatus,
        message = DEFAULT_ERROR_MESSAGE,
      } = error;
      if (dataStatus !== 200) return cb({ error: { errorCode, message } });
      return cb(handleError(error, req));
    }
  }
}

export function anonymousLogin(req, body, profile) {
  // TODO remove this! This IE is temporary til IE is sunset
  if (isBrowserIE()) {
    return new Promise(async (resolve, reject) => {
      const { route, headers, setUserAuthData } = req;
      const { deviceId } = body;
      if (!route || !headers || !body || !deviceId) reject();
      try {
        const response = await axios.post(route, body, headers);
        const data = _.get(response, 'data');
        const status = _.get(response, 'status');
        const dataStatus = _.get(data, 'status', status);
        const authorizationToken = _.get(data, 'accessToken', null);
        if (data && dataStatus === 200) {
          setUserAuthData({
            anonymousAccount: data,
            authorizationToken,
          });
          saveCookie(data, ANONYMOUS_COOKIE_NAME);
          resolve({ data });
        }
      } catch (error) {
        reject(new Error(error));
      }
    });
  }
  return new Promise(async (resolve, reject) => {
    const { route, headers, setUserAuthData } = req;
    const { deviceId } = body;
    if (!route || !headers || !body || !deviceId) reject();
    try {
      const response = await profile?.loginAnonymously({
        deviceId,
        additionalHeaders: v1Override,
      });
      const authorizationToken = response?.accessToken || null;
      if (response) {
        setUserAuthData({
          anonymousAccount: response,
          authorizationToken,
        });
        saveCookie(response, ANONYMOUS_COOKIE_NAME);
        resolve({ data: response });
      }
    } catch (error) {
      reject(new Error(error));
    }
  });
}

export const getFavorites = ({ route, headers }) => {
  const nonCacheableRouteForIE11 = `${route}?timestamp=${new Date().getTime()}`;
  return new Promise((resolve, reject) => {
    axios
      .get(nonCacheableRouteForIE11, headers)
      .then(response => {
        resolve(response.data);
      })
      .catch(reject);
  });
};

export const getV1Favorites = options => {
  const { account, route, headers } = options;
  const { profileId } = account;
  const favoritesV1Route = route.replace('/favorites', `/profiles/${profileId}/favorites`);
  return new Promise((resolve, reject) => {
    axios
      .get(favoritesV1Route, headers)
      .then(response => {
        resolve(response.data);
      })
      .catch(reject);
  });
};

export const setFavorites = ({ favoritableItems, profileFavorites }, isPersonality = false) =>
  new Promise((resolve, reject) => {
    favoritableItems.forEach(async item => {
      try {
        const response = await profileFavorites.create({
          favoriteType: isPersonality ? FAVORITES_TYPE_PERSONALITY : item['@type'],
          favoriteID: item.id,
        });
        resolve(response);
      } catch (error) {
        reject(error);
      }
    });
  });

// TODO remove this! This IE is temporary til IE is sunset
export const setFavoritesIE = ({ route, headers, favoritableItems }, isPersonality = false) =>
  new Promise((resolve, reject) => {
    const favorites = [];
    favoritableItems.forEach(item => {
      favorites.push({
        favoriteType: isPersonality ? FAVORITES_TYPE_PERSONALITY : item['@type'],
        favoriteID: item.id,
      });
    });

    const body = [...favorites];
    axios
      .post(route, body, headers)
      .then(response => {
        const data = _.get(response, 'data', null);
        const status = _.get(response, 'status', null);
        if (data && status === 200) {
          resolve(data);
        }
        reject(new Error('Unable to set Favorite'));
      })
      .catch(reject);
  });

export const deleteFavorites = ({ favoritableItems, profileFavorites }) =>
  new Promise((resolve, reject) => {
    favoritableItems.forEach(async item => {
      try {
        await profileFavorites.remove({ favoriteID: item.id });
        resolve(true);
      } catch (error) {
        reject(error);
      }
    });
  });

// TODO remove this! This IE is temporary til IE is sunset
export const deleteFavoritesIE = ({ route, headers, favoritableItems }) =>
  new Promise((resolve, reject) => {
    const favorites = [];
    favoritableItems.forEach(item => {
      favorites.push({ favoriteID: item.id });
    });
    const body = [...favorites];
    const axiosOptions = {
      url: route,
      method: 'delete',
      data: body,
      headers: headers.headers,
    };
    axios(axiosOptions)
      .then(response => {
        const status = _.get(response, 'data.statusCode', null);
        if (status >= 200 && status <= 299) resolve(true);
        reject(new Error('Error deleting favorite'));
      })
      .catch(reject);
  });

export function logoutClientless({ profile }) {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await profile.logout();
      resolve(response);
    } catch (error) {
      reject(error);
    }
  });
}

export function logoutClientlessIE({ headers, route }) {
  const axiosData = {
    method: 'get',
    url: route,
    headers,
  };
  return new Promise(async (resolve, reject) => {
    try {
      const response = await axios(axiosData);
      resolve(_.get(response, 'data'));
    } catch (error) {
      reject(error);
    }
  });
}

export function toggleAccountExpired() {
  const accountExpired = this.state;
  this.setState({ accountExpired: !accountExpired }, () => true);
  return false;
}

export async function reset(req, body, cb) {
  const { profile } = req;
  if (body) {
    const { email } = body;

    if (!isEmail(email)) return cb({ error: { message: 'Invalid Email' } });
    try {
      await profile.resetPassword({
        additionalHeaders: v1Override,
        email,
      });
      return cb({ success: true });
    } catch (error) {
      const { errorMessage } = error;
      if (errorMessage) {
        return cb({
          error: {
            message: 'No profile found for this email address. Please try again.',
          },
        });
      }
      cb(handleError(error, req));
    }
  }
  return cb({ error: {} });
}

// TODO remove this! This IE is temporary til IE is sunset
export function resetIE(req, body, cb) {
  const { route, headers } = req;
  if (route && headers && body) {
    const { email } = body;

    if (!isEmail(email)) return cb({ error: { message: 'Invalid Email' } });

    return axios
      .post(route, body, headers)
      .then(res => {
        const data = _.get(res, 'data', null);
        const status = _.get(res, 'status', null);

        if (data && status === 200) {
          const errorCode = _.get(data, 'errorCode');
          if (errorCode) {
            return cb({
              error: {
                message: 'No profile found for this email address. Please try again.',
              },
            });
          }

          return cb({ success: true });
        }
        return null;
      })
      .catch(error => cb(handleError(error, req)));
  }
  return cb({ error: {} });
}

export function resetConfirm(req, body, cb) {
  const { route, headers } = req;
  if (route && headers && body) {
    const { email, password, confirmPassword, code } = body;

    if (!code) return cb({ error: { message: 'invalid code' } });
    if (!isValidPassword(password)) return cb({ error: { message: 'invalid password' } });
    if (password !== confirmPassword) return cb({ error: { message: 'Passwords do not match' } });
    const bodyParams = email ? { email, password, code } : { password, code };

    return axios
      .post(route, bodyParams, headers)
      .then(res => {
        const status = _.get(res, 'status', null);
        if (status === 200) {
          return cb({ success: true });
        }
        const message = _.get(res, 'detail');
        return cb({ error: { message } });
      })
      .catch(error => {
        cb(handleError(JSON.parse(JSON.stringify(error)), req));
      });
  }
  return cb({ error: {} });
}

export function deleteAccount(req, body, cb) {
  const { route, headers } = req;
  if (route && headers && body) {
    const { profileId } = body;
    if (!profileId) return cb({ error: { message: 'A profileId is required.' } });

    return axios({
      url: route,
      method: 'delete',
      data: {},
      headers: headers.headers,
    })
      .then(res => {
        const status = _.get(res, 'status', null);
        if (status >= 200 && status <= 299) {
          return cb({ success: true });
        }
        return null;
      })
      .catch(error => cb(handleError(error, { route, body, headers })));
  }
  return cb({ error: {} });
}

export async function editAccount(req, body, cb) {
  const { profile } = req;
  if (body) {
    try {
      const data = await profile.update(body);
      return cb({ success: true, data });
    } catch (error) {
      return cb(handleError(error, req, false));
    }
  }
  return cb({ error: {} });
}

// TODO remove this! This IE is temporary til IE is sunset
export function editAccountIE(req, body, cb) {
  const { route, headers } = req;
  if (route && headers && body) {
    return axios
      .put(route, body, headers)
      .then(res => {
        const { status, data } = res;
        if (status === 200) {
          return cb({ success: true, data });
        }
        return null;
      })
      .catch(error => cb(handleError(error, req)));
  }
  return cb({ error: {} });
}

export function getAccount(req, body, cb) {
  const { route, headers } = req;
  if (route && headers && body) {
    return axios
      .get(route, body, headers)
      .then(res => {
        const status = res.status || null;
        const data = res.data || null;
        if (status === 200) {
          setAccountCookie(data);
          return cb({ success: true, data });
        }
        return null;
      })
      .catch(error => cb(handleError(error, req)));
  }
  return cb({ error: {} });
}

export function getAccountGender({ account, language }) {
  const genderOptions = getGenderOptions(language);
  const validGenders = _.map(genderOptions, 'value');
  const accountGender = _.get(account, 'gender', null);
  return _.includes(validGenders, accountGender) ? accountGender : null;
}

export function getBirthdateIsoFormat(date) {
  const monthStr = date.substring(0, 2);
  const dayStr = date.substring(2, 4);
  const yearStr = date.substring(4);
  const isoFormat = `${yearStr}-${monthStr}-${dayStr}`;
  const isFullDate = isoFormat.length === 10;
  return isFullDate ? isoFormat : null;
}

export function getBirthdateIso8106UTCFormat(date) {
  if (date.length !== 8) return null;

  const monthStr = date.substring(0, 2);
  const dayStr = date.substring(2, 4);
  const yearStr = date.substring(4);
  const parsedDate = `${yearStr}-${monthStr}-${dayStr}`;

  const iso8106UTC = format(startOfDay(stringToDate(parsedDate)), FORMAT_ISO);

  return iso8106UTC;
}
